import { fetchErpCurrencies } from '@/lib/api/erpCurrencies';
import { graphqlQuery } from '@/lib/api/graphql';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { CurrencyApi } from '@/types/api/companies';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

/**
 * Fallback currencies used when the API is unavailable (also doubles as demo-mode data).
 * This is the single source of truth for hardcoded currency metadata in the frontend.
 */
export const FALLBACK_CURRENCIES: CurrencyApi[] = [
  { id: 1, code: 'GBP', name: 'British Pound', symbol: '£' },
  { id: 2, code: 'EUR', name: 'Euro', symbol: '€' },
  { id: 3, code: 'USD', name: 'US Dollar', symbol: '$' },
  { id: 4, code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { id: 5, code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { id: 6, code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { id: 7, code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
  { id: 8, code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { id: 9, code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { id: 10, code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { id: 11, code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { id: 12, code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { id: 13, code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { id: 14, code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { id: 15, code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { id: 16, code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { id: 17, code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { id: 18, code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { id: 19, code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { id: 20, code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { id: 21, code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { id: 22, code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { id: 23, code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { id: 24, code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { id: 25, code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { id: 26, code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { id: 27, code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { id: 28, code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { id: 29, code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { id: 30, code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { id: 31, code: 'TWD', name: 'New Taiwan Dollar', symbol: 'NT$' },
  { id: 32, code: 'ZAR', name: 'South African Rand', symbol: 'R' },
];

interface CurrencyNode {
  currencyId: number;
  code: string;
  symbol: string | null;
  name: string | null;
}

interface CurrencyListGraphQLResponse {
  currencies: {
    currencyList: {
      edges: Array<{ node: CurrencyNode }>;
    };
  };
}

const CURRENCY_LIST_QUERY = `
  query CurrencyList($first: Int) {
    currencies {
      currencyList(first: $first) {
        edges {
          node {
            currencyId
            code
            symbol
            name
          }
        }
      }
    }
  }
`;

function mapNodeToCurrencyApi(node: CurrencyNode): CurrencyApi {
  return {
    id: node.currencyId,
    code: node.code,
    name: node.name ?? node.code,
    symbol: node.symbol,
  };
}

export function useCurrenciesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CurrencyApi[], Error>(
    ['currencies'],
    async ({ signal }) => {
      return apiGet<CurrencyApi[]>('/api/v1/currencies', undefined, signal);
    },
    FALLBACK_CURRENCIES,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}

export function useBankDetailsCurrencies(
  linkedSubscriptionId: string | null | undefined,
  linkedProductId: string | null | undefined,
) {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<CurrencyApi[], Error>(
    ['currencies', 'bankDetails', linkedSubscriptionId ?? '', linkedProductId ?? ''],
    async ({ signal }) => {
      try {
        const response = await graphqlQuery<CurrencyListGraphQLResponse>(
          apiConfig,
          CURRENCY_LIST_QUERY,
          { first: 0 },
          signal,
        );
        const list = response.currencies.currencyList.edges.map((x) => mapNodeToCurrencyApi(x.node));
        if (list.length > 0) return list;
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') throw err;
        // GraphQL failed (e.g. no company selected yet) — fall through to ERP
      }

      if (linkedSubscriptionId && linkedProductId) {
        try {
          const erpList = await fetchErpCurrencies(apiConfig, linkedSubscriptionId, linkedProductId, signal);
          if (erpList.length > 0) return erpList;
        } catch (err) {
          if ((err as DOMException)?.name === 'AbortError') throw err;
        }
      }

      return FALLBACK_CURRENCIES;
    },
    FALLBACK_CURRENCIES,
    {
      staleTime: 5 * 60 * 1000,
      skipCompanyCheck: true,
    },
  );
}
